import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'styled-components';
import { lightTheme } from '@themes/lightTheme';
import GlobalStyle from '@themes/GlobalStyle';
import DataDogErrorBoundary from '@utils/services/datadog/DataDogErrorBoundary';
import { LocaleProvider } from '@utils/locale/locale';
import { TestableProvider } from './utils/testable/testable';
import { SessionProvider } from '@utils/context/session-provider';
import { AppRouter } from './router';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { initialiseDataDog } from '@utils/services/datadog/datadog-helpers';
import {
  initialiseDynatrace,
  loadDynatraceScript,
} from '@utils/services/dynatrace/dynatrace-helpers';
import { getTestableFeaturesEnabled } from '@utils/testable/testable.helpers';
import { IS_PRODUCTION } from 'utils/check-environment/check-environment';
import { LocalStorageKeys } from 'utils/localStorage';
import { AppLogger } from '@workivate/tho-web-shared';

// Initialize DataDog if enabled
if (import.meta.env.DD_APP_ID && import.meta.env.DD_CLIENT_TOKEN) {
  initialiseDataDog();
}

const dynatraceEnabled =
  localStorage.getItem(LocalStorageKeys.dynatraceEnabled) === 'true';

if (getTestableFeaturesEnabled() && !IS_PRODUCTION && dynatraceEnabled) {
  void (async () => {
    try {
      const scriptUrl = import.meta.env.DYNATRACE_SCRIPT_URL;

      if (!scriptUrl) {
        AppLogger.logWithOptions({
          message:
            '[DT] DYNATRACE_SCRIPT_URL is not set; skipping Dynatrace init',
        });
      } else {
        await loadDynatraceScript(import.meta.env.DYNATRACE_SCRIPT_URL);
        initialiseDynatrace();
      }
    } catch (e) {
      AppLogger.logWithOptions({
        message: '[DT] Failed to load Dynatrace script',
        error: e,
      });
    }
  })();
}

export const App: React.FC = () => {
  // At present USL has only light theme. If we ever add dark theme support,
  // we can make this dynamic based on user preference or system settings.
  const theme = lightTheme;

  // SessionProvider must be ancestor of LocaleProvider not descendant
  // to avoid reinitialising session context on locale change
  return (
    <HelmetProvider>
      <DataDogErrorBoundary fallback={<div>An error occurred</div>}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <TestableProvider>
              <SessionProvider>
                <LocaleProvider>
                  <GlobalStyle />
                  <ErrorBoundary>
                    <AppRouter />
                  </ErrorBoundary>
                </LocaleProvider>
              </SessionProvider>
            </TestableProvider>
          </BrowserRouter>
        </ThemeProvider>
      </DataDogErrorBoundary>
    </HelmetProvider>
  );
};
